export interface CustomizationHeightValue {
  id: string;
  value: string;
  label: string;
}
export type CustomizationProhibitedProperty =
  | { key: string; match: 'ANY_NONEMPTY' }
  | { key: string; match: 'ONE_OF'; values: string[] };
export interface CustomizationConfiguration {
  optionName: string;
  standardOptionValue: string;
  customizedOptionValue: string;
  heightPropertyKey: string;
  heightLabel: string;
  heightValues: CustomizationHeightValue[];
  originalClassification: {
    customizedSkuSuffix: string;
    prohibitedProperties: CustomizationProhibitedProperty[];
    prohibitedProductIds: string[];
    ignoredPropertyKeys: string[];
  };
  originalRestocking: {
    currency: 'USD';
    amountPerEligibleUnit: '25.00';
    standardExempt: true;
  };
  replacementReturnPolicy: 'NO_REPEAT_RETURN_OR_EXCHANGE';
  fulfillmentMode: 'MANUAL_TEAM_ACK';
}
export interface CustomizationDraft {
  enabled: boolean;
  configuration: CustomizationConfiguration | null;
}
export interface CustomizationPolicy extends CustomizationDraft {
  version: 1;
  storeId: string;
  revision: number;
  createdAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}
const uuid = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value);
function demand(value: unknown): asserts value {
  if (!value)
    throw new Error('Customized garment configuration could not be verified.');
}
function record(
  value: unknown,
  required: string[],
  optional: string[] = [],
): Record<string, any> {
  demand(value && typeof value === 'object' && !Array.isArray(value));
  const prototype = Object.getPrototypeOf(value),
    fields = Object.getOwnPropertyDescriptors(value);
  demand(prototype === Object.prototype || prototype === null);
  demand(
    Reflect.ownKeys(fields).length >= required.length &&
      Reflect.ownKeys(fields).length <= required.length + optional.length &&
      required.every((key) => Object.hasOwnProperty.call(fields, key)),
  );
  demand(
    Reflect.ownKeys(fields).every(
      (key) =>
        typeof key === 'string' &&
        [...required, ...optional].includes(key) &&
        'value' in fields[key] &&
        fields[key].enumerable,
    ),
  );
  return Object.fromEntries(
    Object.entries(fields).map(([key, field]) => [key, field.value]),
  );
}
function text(value: unknown, max = 120, preserveWhitespace = false): string {
  demand(
    typeof value === 'string' &&
      value.length > 0 &&
      value.length <= max &&
      value.trim().length > 0 &&
      !/[\p{Cc}\p{Cf}]/u.test(value) &&
      (preserveWhitespace || value === value.trim()),
  );
  return value;
}
function array(value: unknown, max: number, minimum = 0): unknown[] {
  demand(
    Array.isArray(value) && value.length >= minimum && value.length <= max,
  );
  const fields = Object.getOwnPropertyDescriptors(value);
  demand(Reflect.ownKeys(fields).length === value.length + 1);
  return Array.from({ length: value.length }, (_, index) => {
    const field = fields[String(index)];
    demand(field && 'value' in field);
    return field.value;
  });
}
function unique(values: string[]) {
  demand(new Set(values).size === values.length);
}
export function parseCustomizationConfiguration(
  value: unknown,
): CustomizationConfiguration {
  const raw = record(value, [
    'optionName',
    'standardOptionValue',
    'customizedOptionValue',
    'heightPropertyKey',
    'heightLabel',
    'heightValues',
    'originalClassification',
    'originalRestocking',
    'replacementReturnPolicy',
    'fulfillmentMode',
  ]);
  const optionName = text(raw.optionName),
    standardOptionValue = text(raw.standardOptionValue),
    customizedOptionValue = text(raw.customizedOptionValue),
    heightPropertyKey = text(raw.heightPropertyKey, 64),
    heightLabel = text(raw.heightLabel);
  demand(standardOptionValue !== customizedOptionValue);
  const heightValues = array(raw.heightValues, 32, 1).map((value) => {
    const height = record(value, ['id', 'value', 'label']);
    demand(
      typeof height.id === 'string' &&
        /^[a-z0-9][a-z0-9_-]{0,63}$/.test(height.id),
    );
    return {
      id: height.id as string,
      value: text(height.value, 120, true),
      label: text(height.label),
    };
  });
  unique(heightValues.map((height) => height.id));
  unique(heightValues.map((height) => height.value));
  const original = record(
    raw.originalClassification,
    ['customizedSkuSuffix', 'prohibitedProperties', 'prohibitedProductIds'],
    ['ignoredPropertyKeys'],
  );
  const customizedSkuSuffix = text(original.customizedSkuSuffix, 32);
  const prohibitedProperties: CustomizationProhibitedProperty[] = array(
    original.prohibitedProperties,
    32,
  ).map((value) => {
    const property = record(value, ['key', 'match'], ['values']),
      key = text(property.key, 64);
    demand(key !== heightPropertyKey);
    if (property.match === 'ANY_NONEMPTY') {
      demand(!Object.hasOwnProperty.call(property, 'values'));
      return { key, match: 'ANY_NONEMPTY' };
    }
    demand(property.match === 'ONE_OF');
    const values = array(property.values, 32, 1).map((value) => text(value));
    unique(values);
    return { key, match: 'ONE_OF', values };
  });
  unique(prohibitedProperties.map((property) => property.key));
  const prohibitedProductIds = array(original.prohibitedProductIds, 100).map(
    (value) => {
      demand(
        typeof value === 'string' &&
          value.length <= 160 &&
          /^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(value),
      );
      return value;
    },
  );
  unique(prohibitedProductIds);
  const ignoredPropertyKeys = array(
    original.ignoredPropertyKeys === undefined
      ? []
      : original.ignoredPropertyKeys,
    32,
  ).map((value) => text(value, 64));
  unique(ignoredPropertyKeys);
  demand(
    ignoredPropertyKeys.every(
      (key) =>
        key !== heightPropertyKey &&
        !prohibitedProperties.some((property) => property.key === key),
    ),
  );
  const restocking = record(raw.originalRestocking, [
    'currency',
    'amountPerEligibleUnit',
    'standardExempt',
  ]);
  demand(
    restocking.currency === 'USD' &&
      restocking.amountPerEligibleUnit === '25.00' &&
      restocking.standardExempt === true &&
      raw.replacementReturnPolicy === 'NO_REPEAT_RETURN_OR_EXCHANGE' &&
      raw.fulfillmentMode === 'MANUAL_TEAM_ACK',
  );
  return {
    optionName,
    standardOptionValue,
    customizedOptionValue,
    heightPropertyKey,
    heightLabel,
    heightValues,
    originalClassification: {
      customizedSkuSuffix,
      prohibitedProperties,
      prohibitedProductIds,
      ignoredPropertyKeys,
    },
    originalRestocking: {
      currency: 'USD',
      amountPerEligibleUnit: '25.00',
      standardExempt: true,
    },
    replacementReturnPolicy: 'NO_REPEAT_RETURN_OR_EXCHANGE',
    fulfillmentMode: 'MANUAL_TEAM_ACK',
  };
}
export function parseCustomizationPolicy(
  value: unknown,
  storeId: string,
): CustomizationPolicy | null {
  try {
    demand(uuid(storeId));
    const raw = record(
      value,
      [
        'version',
        'storeId',
        'revision',
        'enabled',
        'configuration',
        'createdAt',
        'updatedAt',
        'updatedBy',
      ],
      ['__typename'],
    );
    demand(
      (raw.__typename === undefined ||
        raw.__typename === 'ReturnExchangeCustomizationPolicySettings') &&
        raw.version === 1 &&
        raw.storeId === storeId &&
        typeof raw.enabled === 'boolean',
    );
    if (raw.revision === 0) {
      demand(
        raw.enabled === false &&
          raw.configuration === null &&
          raw.createdAt === null &&
          raw.updatedAt === null &&
          raw.updatedBy === null,
      );
      return {
        version: 1,
        storeId,
        revision: 0,
        enabled: false,
        configuration: null,
        createdAt: null,
        updatedAt: null,
        updatedBy: null,
      };
    }
    demand(
      Number.isSafeInteger(raw.revision) &&
        raw.revision >= 1 &&
        raw.revision <= 2147483647 &&
        uuid(raw.updatedBy),
    );
    for (const value of [raw.createdAt, raw.updatedAt])
      demand(
        typeof value === 'string' &&
          value.length <= 40 &&
          /^\d{4}-\d\d-\d\dT/.test(value) &&
          Number.isFinite(Date.parse(value)),
      );
    demand(
      Date.parse(raw.updatedAt) >= Date.parse(raw.createdAt) &&
        (raw.configuration !== null || raw.enabled === false),
    );
    return {
      version: 1,
      storeId,
      revision: raw.revision,
      enabled: raw.enabled,
      configuration:
        raw.configuration === null
          ? null
          : parseCustomizationConfiguration(raw.configuration),
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
      updatedBy: raw.updatedBy,
    };
  } catch {
    return null;
  }
}
export function validateCustomizationDraft(
  value: CustomizationDraft,
): string | null {
  try {
    demand(
      typeof value.enabled === 'boolean' &&
        (value.configuration !== null || value.enabled === false),
    );
    if (value.configuration !== null)
      parseCustomizationConfiguration(value.configuration);
    return null;
  } catch {
    return 'Complete the customized garment configuration with distinct options, height choices and valid exclusion fields before saving.';
  }
}
function stable(value: any): any {
  return Array.isArray(value)
    ? value.map(stable)
    : value && typeof value === 'object'
    ? Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((key) => [key, stable(value[key])]),
      )
    : value;
}
export function customizationPolicyKey(value: CustomizationDraft): string {
  return JSON.stringify(
    stable({ enabled: value.enabled, configuration: value.configuration }),
  );
}
